import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Avatar,
  Divider,
  List,
  ListItem,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tabs,
  Tab,
  Alert,
  Menu,
} from '@mui/material';
import {
  ArrowBack,
  LiveTv,
  Schedule,
  CheckCircle,
  LocationOn,
  Person,
  AccessTime,
  Add,
  EmojiEvents,
  MoreVert,
  PlayArrow,
  Share,
} from '@mui/icons-material';
import {
  useMatch,
  useMatchEvents,
  useAddMatchEvent,
  useUpdateMatchEvent,
  useDeleteMatchEvent,
  useTournament,
} from '@/hooks/useApi';
import { LoadingState, ErrorState, ResponsiveDialog } from '@/components';
import { BOTTOM_NAV_OFFSET } from '@/components/BottomNav';
import {
  defaultEventTypeKey,
  eventTypeOptions,
  getEventOptionByKey,
  getEventTypeKeyFromEvent as eventTypeKeyFrom,
  matchEventIcon,
  matchEventLabel,
} from '@/components/matchEventVisuals';
import { getApiErrorMessage } from '@/utils/apiError';
import { matchScoreView } from '@/utils/matchScore';
import { useAdminMode } from '@/hooks/useManage';
import { KebabMenu, shareLink } from '@/components/manage';
import { useNotification } from '@/contexts/NotificationContext';
import type { MatchEventType, Team, Player } from '@/types';

const getStatusConfig = (status: string) => {
  switch (status) {
    case 'live': return { color: '#ff4444', text: 'LIVE', icon: <LiveTv /> };
    case 'finished': return { color: '#4caf50', text: 'Завершен', icon: <CheckCircle /> };
    case 'scheduled': return { color: '#ffda00', text: 'Запланирован', icon: <Schedule /> };
    case 'canceled': return { color: '#808080', text: 'Отменен', icon: <AccessTime /> };
    default: return { color: '#b0b0b0', text: status, icon: <AccessTime /> };
  }
};

type EventFormState = {
  type: MatchEventType;
  timestampSec: number;
  actorId?: string;
  targetId?: string;
  period: number;
};

type EventTeamSide = '' | 'home' | 'away';

type EventActorOption = {
  id: string;
  label: string;
};

type MatchEventView = {
  id: string;
  type: MatchEventType;
  timestampSec?: number;
  period?: number;
  actorId?: string | null;
  targetId?: string | null;
  meta?: Record<string, unknown>;
};

const getEventLabelFromMeta = (event: MatchEventView): string => matchEventLabel(event.type, event.meta);
const getEventIconFromMeta = (event: MatchEventView): React.ReactNode => matchEventIcon(event.type, event.meta);
const getEventTypeKeyFromEvent = (event: MatchEventView): string => eventTypeKeyFrom(event.type, event.meta);

const getEventTeamSide = (event: MatchEventView): EventTeamSide =>
  event.meta?.teamSide === 'home' || event.meta?.teamSide === 'away'
    ? event.meta.teamSide
    : '';

const getParticipantName = (participant: Team | Player | undefined): string => {
  if (!participant) return 'TBD';
  if ('firstName' in participant) {
    return `${participant.firstName} ${participant.lastName || ''}`.trim();
  }
  return participant.name;
};

const isTeamParticipant = (participant: Team | Player | undefined): participant is Team => {
  return Boolean(participant && 'name' in participant);
};

const getInitial = (name: string): string => {
  return name.trim().charAt(0).toUpperCase() || '?';
};

const parseMinuteToSeconds = (value: string): number => {
  const minute = Number.parseInt(value, 10);
  if (!Number.isFinite(minute) || minute < 0) return 0;
  return minute * 60;
};

export const MatchDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [eventDialogMode, setEventDialogMode] = useState<'create' | 'edit'>('create');
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [eventTypeKey, setEventTypeKey] = useState(defaultEventTypeKey);
  const [eventForm, setEventForm] = useState<EventFormState>({
    type: getEventOptionByKey(defaultEventTypeKey).value,
    timestampSec: 0,
    actorId: undefined,
    targetId: undefined,
    period: 1,
  });
  const [eventMinute, setEventMinute] = useState('0');
  const [eventPeriodInput, setEventPeriodInput] = useState('1');
  const [eventAddedTimeInput, setEventAddedTimeInput] = useState('');
  const [eventTeamSide, setEventTeamSide] = useState<EventTeamSide>('');
  const [eventOwnGoal, setEventOwnGoal] = useState(false);
  const [eventActionsAnchorEl, setEventActionsAnchorEl] = useState<null | HTMLElement>(null);
  const [activeEventForActions, setActiveEventForActions] = useState<MatchEventView | null>(null);
  const [actionError, setActionError] = useState('');

  const { data: match, isLoading: matchLoading, error: matchError, refetch: refetchMatch } = useMatch(id || '');
  const { data: events, refetch: refetchEvents } = useMatchEvents(id || '');
  const { data: tournament } = useTournament(match?.tournamentId || '', { enabled: Boolean(match?.tournamentId) });
  const { can } = useAdminMode();
  const { showSuccess } = useNotification();
  const addEvent = useAddMatchEvent();
  const updateEvent = useUpdateMatchEvent();
  const deleteEvent = useDeleteMatchEvent();

  const participantsSource = (match?.participants && match.participants.length > 0)
    ? match.participants
    : tournament?.participants;
  const participants = (participantsSource || []) as (Team | Player)[];
  const homeParticipant = match?.homeId ? participants.find((p) => p.id === match.homeId) : undefined;
  const awayParticipant = match?.awayId ? participants.find((p) => p.id === match.awayId) : undefined;
  const homeName = homeParticipant ? getParticipantName(homeParticipant) : 'Хозяева';
  const awayName = awayParticipant ? getParticipantName(awayParticipant) : 'Гости';
  const homeRoster = isTeamParticipant(homeParticipant) ? homeParticipant.roster || [] : [];
  const awayRoster = isTeamParticipant(awayParticipant) ? awayParticipant.roster || [] : [];

  const sideParticipants = {
    home: homeParticipant,
    away: awayParticipant,
  } as const;
  const oppositeSide: EventTeamSide =
    eventTeamSide === 'home' ? 'away' : eventTeamSide === 'away' ? 'home' : '';
  const actorSide: EventTeamSide =
    eventForm.type === 'goal' && eventOwnGoal ? oppositeSide : eventTeamSide;
  const selectedParticipants =
    eventForm.type === 'goal' && eventOwnGoal && !actorSide
      ? []
      : actorSide === 'home'
        ? [sideParticipants.home]
        : actorSide === 'away'
          ? [sideParticipants.away]
          : [sideParticipants.home, sideParticipants.away];
  const actorOptions: EventActorOption[] = selectedParticipants.flatMap((participant) => {
    if (!participant) return [];
    if ('firstName' in participant) {
      return [{ id: participant.id, label: getParticipantName(participant) }];
    }

    const teamName = participant.name;
    const roster = participant.roster || [];
    return roster.map((player) => ({
      id: player.id,
      label: `${player.firstName} ${player.lastName || ''}`.trim() + ` (${teamName})`,
    }));
  });
  const assistantParticipants =
    eventTeamSide === 'home'
      ? [sideParticipants.home]
      : eventTeamSide === 'away'
        ? [sideParticipants.away]
        : [];
  const assistantOptions: EventActorOption[] = assistantParticipants.flatMap((participant) => {
    if (!participant) return [];
    if ('firstName' in participant) {
      return [{ id: participant.id, label: getParticipantName(participant) }];
    }
    const teamName = participant.name;
    const roster = participant.roster || [];
    return roster.map((player) => ({
      id: player.id,
      label: `${player.firstName} ${player.lastName || ''}`.trim() + ` (${teamName})`,
    }));
  });
  const actorNameById = new Map<string, string>();
  const actorTeamById = new Map<string, string>();
  participants.forEach((participant) => {
    if ('firstName' in participant) {
      actorNameById.set(participant.id, getParticipantName(participant));
      actorTeamById.set(participant.id, homeParticipant?.id === participant.id ? homeName : awayParticipant?.id === participant.id ? awayName : 'Участник');
      return;
    }
    actorNameById.set(participant.id, participant.name);
    actorTeamById.set(participant.id, participant.name);
    const roster = participant.roster || [];
    roster.forEach((player) => {
      actorNameById.set(player.id, `${player.firstName} ${player.lastName || ''}`.trim());
      actorTeamById.set(player.id, participant.name);
    });
  });
  const eventTeamLabel = (event: MatchEventView): string => {
    if (event.meta?.eventCode === 'added_time') return '';
    const side = getEventTeamSide(event);
    if (side === 'home') return homeName;
    if (side === 'away') return awayName;
    if (event.actorId && actorTeamById.get(event.actorId)) return actorTeamById.get(event.actorId) as string;
    return '';
  };

  const isVarCancelled = (event: MatchEventView): boolean => event.meta?.varCancelled === true;
  const isVarCancelableEvent = (event: MatchEventView): boolean =>
    event.type === 'goal' || event.type === 'card_yellow' || event.type === 'card_red';

  const resetEventDialogState = () => {
    setShowEventDialog(false);
    setEventDialogMode('create');
    setEditingEventId(null);
    setActiveEventForActions(null);
    setEventActionsAnchorEl(null);
    setEventTypeKey(defaultEventTypeKey);
    setEventMinute('0');
    setEventPeriodInput('1');
    setEventAddedTimeInput('');
    setEventTeamSide('');
    setEventOwnGoal(false);
    setEventForm({
      type: getEventOptionByKey(defaultEventTypeKey).value,
      timestampSec: 0,
      actorId: undefined,
      targetId: undefined,
      period: 1,
    });
  };

  const closeEventActions = () => {
    setEventActionsAnchorEl(null);
  };

  const openEventActions = (event: MatchEventView, anchor: HTMLElement) => {
    setActiveEventForActions(event);
    setEventActionsAnchorEl(anchor);
  };

  const openCreateEventDialog = () => {
    resetEventDialogState();
    setActiveEventForActions(null);
    setShowEventDialog(true);
  };

  const handleEditEvent = (event: MatchEventView) => {
    const key = getEventTypeKeyFromEvent(event);
    const side = getEventTeamSide(event);
    const ownGoal = event.meta?.ownGoal === true;
    setEventDialogMode('edit');
    setEditingEventId(event.id);
    setEventTypeKey(key);
    setEventTeamSide(side);
    setEventOwnGoal(ownGoal);
    setEventMinute(String(Math.floor((event.timestampSec || 0) / 60)));
    setEventPeriodInput(event.period != null ? String(event.period) : '');
    setEventAddedTimeInput(typeof event.meta?.addedMinutes === 'number' ? String(event.meta.addedMinutes) : '');
    setEventForm({
      type: getEventOptionByKey(key).value,
      timestampSec: event.timestampSec || 0,
      actorId: event.actorId || undefined,
      targetId: event.targetId || undefined,
      period: event.period || 1,
    });
    closeEventActions();
    setShowEventDialog(true);
  };

  const handleDuplicateEvent = (event: MatchEventView) => {
    const key = getEventTypeKeyFromEvent(event);
    const side = getEventTeamSide(event);
    const ownGoal = event.meta?.ownGoal === true;
    setEventDialogMode('create');
    setEditingEventId(null);
    setEventTypeKey(key);
    setEventTeamSide(side);
    setEventOwnGoal(ownGoal);
    setEventMinute(String(Math.floor((event.timestampSec || 0) / 60)));
    setEventPeriodInput(event.period != null ? String(event.period) : '');
    setEventAddedTimeInput(typeof event.meta?.addedMinutes === 'number' ? String(event.meta.addedMinutes) : '');
    setEventForm({
      type: getEventOptionByKey(key).value,
      timestampSec: event.timestampSec || 0,
      actorId: event.actorId || undefined,
      targetId: event.targetId || undefined,
      period: event.period || 1,
    });
    closeEventActions();
    setShowEventDialog(true);
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (confirm('Удалить событие?')) {
      await deleteEvent.mutateAsync({ matchId: id!, eventId });
      refetchEvents();
      refetchMatch();
    }
  };

  const handleVarCancel = async (event: MatchEventView) => {
    setActionError('');
    try {
      await updateEvent.mutateAsync({
        matchId: id!,
        eventId: event.id,
        data: {
          meta: {
            ...(event.meta || {}),
            varCancelled: true,
            varDecision: 'cancelled',
          },
        },
      });
      refetchEvents();
      refetchMatch();
      closeEventActions();
    } catch (error) {
      setActionError(getApiErrorMessage(error, 'Не удалось отменить событие по VAR.'));
    }
  };

  const handleSubmitEvent = async () => {
    setActionError('');
    const selectedEventOption = getEventOptionByKey(eventTypeKey);
    if (eventForm.type === 'goal' && !eventTeamSide) {
      setActionError('Для гола выберите команду события.');
      return;
    }
    if (eventForm.type === 'substitution') {
      if (!eventTeamSide) {
        setActionError('Для замены выберите команду.');
        return;
      }
      if (!eventForm.actorId || !eventForm.targetId) {
        setActionError('Для замены укажите, кто ушел и кто вышел.');
        return;
      }
      if (eventForm.actorId === eventForm.targetId) {
        setActionError('Игроки для замены должны отличаться.');
        return;
      }
    }
    if (eventForm.type === 'goal' && eventOwnGoal && !eventTeamSide) {
      setActionError('Для автогола выберите команду события.');
      return;
    }
    if (selectedEventOption.metaCode === 'added_time') {
      const added = Number.parseInt(eventAddedTimeInput, 10);
      if (!Number.isFinite(added) || added <= 0) {
        setActionError('Укажите, сколько минут добавлено к тайму.');
        return;
      }
    }
    const parsedPeriod = Number.parseInt(eventPeriodInput, 10);
    const addedMinutes = Number.parseInt(eventAddedTimeInput, 10);
    try {
      const payload = {
        ...eventForm,
        period: Number.isFinite(parsedPeriod) && parsedPeriod > 0 ? parsedPeriod : undefined,
        actorId: eventForm.actorId || undefined,
        targetId: eventForm.targetId || undefined,
        meta: (eventTeamSide || (eventForm.type === 'goal' && eventOwnGoal) || selectedEventOption.metaCode)
          ? {
              ...(eventTeamSide ? { teamSide: eventTeamSide } : {}),
              ...(eventForm.type === 'goal' && eventOwnGoal ? { ownGoal: true } : {}),
              ...(selectedEventOption.metaCode ? { eventCode: selectedEventOption.metaCode } : {}),
              ...(selectedEventOption.metaCode === 'added_time' && Number.isFinite(addedMinutes) && addedMinutes > 0 ? { addedMinutes } : {}),
              ...(eventDialogMode === 'edit' && activeEventForActions?.meta?.varCancelled === true ? { varCancelled: true } : {}),
            }
          : undefined,
      };

      if (eventDialogMode === 'edit' && editingEventId) {
        await updateEvent.mutateAsync({
          matchId: id!,
          eventId: editingEventId,
          data: payload,
        });
      } else {
        await addEvent.mutateAsync({
          matchId: id!,
          data: payload,
        });
      }
      resetEventDialogState();
      setActiveEventForActions(null);
      refetchEvents();
      refetchMatch();
    } catch (error) {
      setActionError(getApiErrorMessage(error, eventDialogMode === 'edit' ? 'Не удалось изменить событие.' : 'Не удалось добавить событие.'));
    }
  };

  if (matchLoading) return <LoadingState fullscreen message="Загрузка матча..." />;
  if (matchError || !match) return (
    <Box sx={{ p: 2 }}>
      <IconButton onClick={() => navigate(-1)} sx={{ mb: 2 }}><ArrowBack /></IconButton>
      <ErrorState message="Ошибка загрузки матча" onRetry={refetchMatch} />
    </Box>
  );

  const statusConfig = getStatusConfig(match.status);
  const canManageMatch = can(match.permissions?.reportResult);
  const liveLabel = match.status === 'scheduled' ? 'Начать матч' : match.status === 'live' ? 'Вести матч' : 'Внести счёт';
  const { home: homeScore, away: awayScore, setsLine } = matchScoreView(match.score);
  const sortedEvents = [...((events as MatchEventView[] | undefined) ?? [])].sort((a, b) => {
    const periodA = a.period ?? Number.MAX_SAFE_INTEGER;
    const periodB = b.period ?? Number.MAX_SAFE_INTEGER;
    if (periodA !== periodB) return periodA - periodB;
    const minuteA = a.timestampSec ?? Number.MAX_SAFE_INTEGER;
    const minuteB = b.timestampSec ?? Number.MAX_SAFE_INTEGER;
    return minuteA - minuteB;
  });

  return (
    <Box sx={{ px: 2, pb: 2, pt: 1 }}>
      <Paper sx={{ overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 1, pt: 1 }}>
          <KebabMenu
            items={[
              {
                id: 'share',
                label: 'Поделиться',
                icon: <Share fontSize="small" />,
                onClick: async () => {
                  try {
                    if ((await shareLink(`${homeName} — ${awayName}`)) === 'copied') showSuccess('Ссылка скопирована');
                  } catch {
                    /* отмена системного меню */
                  }
                },
              },
              {
                id: 'correct',
                label: 'Исправить результат',
                hidden: !(match.status === 'finished' && can(match.permissions?.update)),
                onClick: () => navigate(`/matches/${id}/live`),
              },
            ]}
          />
        </Box>
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', gap: 1 }}>
            <Box sx={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  mx: 'auto',
                  mb: 1,
                  bgcolor: 'background.default',
                  color: 'text.primary',
                  fontSize: '1.5rem',
                  borderRadius: 2,
                }}
              >
                {getInitial(homeName)}
              </Avatar>
              <Typography variant="body1" fontWeight="bold">{homeName}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', px: 1, flexShrink: 0 }}>
              {match.scheduledAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {new Date(match.scheduledAt).toLocaleString('ru-RU')}
                </Typography>
              )}
              <Typography variant="h2" fontWeight="bold" sx={{ fontSize: { xs: '2.25rem', sm: '3.25rem' }, lineHeight: 1.1 }}>
                {homeScore} - {awayScore}
              </Typography>
              {setsLine && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {setsLine}
                </Typography>
              )}
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 0.75,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: statusConfig.color,
                }}
              >
                {statusConfig.text}
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  mx: 'auto',
                  mb: 1,
                  bgcolor: 'background.default',
                  color: 'text.primary',
                  fontSize: '1.5rem',
                  borderRadius: 2,
                }}
              >
                {getInitial(awayName)}
              </Avatar>
              <Typography variant="body1" fontWeight="bold">{awayName}</Typography>
            </Box>
          </Box>
        </Box>

        {canManageMatch && actionError && (
          <Alert severity="error" sx={{ mx: 2, mb: 2 }}>{actionError}</Alert>
        )}

        <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="fullWidth">
            <Tab label="События" />
            <Tab label="Информация" />
          </Tabs>
        </Box>

        <Box sx={{ p: 2 }}>
          {activeTab === 0 && canManageMatch && (
            <Button variant="outlined" color="manage" startIcon={<Add />} onClick={openCreateEventDialog} sx={{ mb: 1 }}>
              Добавить событие
            </Button>
          )}
          {activeTab === 0 && (
            !events || events.length === 0 ? (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>Нет событий</Typography>
            ) : (
              <List disablePadding>
                {sortedEvents.map((event, index) => (
                  <React.Fragment key={event.id || index}>
                    <ListItem disableGutters>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <Box
                          sx={{
                            width: 28,
                            minWidth: 28,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {getEventIconFromMeta(event)}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2">
                            {getEventLabelFromMeta(event)}{eventTeamLabel(event) ? ` ${eventTeamLabel(event)}` : ''}
                          </Typography>
                          {event.type === 'substitution' ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Ушел: {event.actorId && actorNameById.get(event.actorId) ? actorNameById.get(event.actorId) : '—'}; Вышел: {event.targetId && actorNameById.get(event.targetId) ? actorNameById.get(event.targetId) : '—'}
                            </Typography>
                          ) : (typeof event.meta?.addedMinutes === 'number') ? (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Добавлено: +{event.meta.addedMinutes} мин
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {event.actorId && actorNameById.get(event.actorId) ? actorNameById.get(event.actorId) : '—'}
                              {event.type === 'goal' && event.targetId && actorNameById.get(event.targetId) ? `; Ассист: ${actorNameById.get(event.targetId)}` : ''}
                            </Typography>
                          )}
                          {event.timestampSec != null && (
                            <Typography variant="caption" color="text.secondary">
                              {Math.floor(event.timestampSec / 60)}' • {event.period ?? 1} тайм
                            </Typography>
                          )}
                          {isVarCancelled(event) && (
                            <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                              Отменено по решению VAR
                            </Typography>
                          )}
                        </Box>
                        {canManageMatch && (
                          <IconButton
                            onClick={(e) => openEventActions(event, e.currentTarget)}
                            aria-label="Действия"
                          >
                            <MoreVert />
                          </IconButton>
                        )}
                      </Box>
                    </ListItem>
                    {index < events.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )
          )}

          {activeTab === 1 && (
            <>
              <Typography variant="subtitle2" gutterBottom>Информация о матче</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {match.round && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><AccessTime fontSize="small" color="action" /><Typography variant="body2">Раунд: {match.round}</Typography></Box>}
                {match.stage && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><EmojiEvents fontSize="small" color="action" /><Typography variant="body2">Стадия: {match.stage}</Typography></Box>}
                {match.venueId && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocationOn fontSize="small" color="action" /><Typography variant="body2">Арена: {match.venueId}</Typography></Box>}
                {match.officials && match.officials.length > 0 && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Person fontSize="small" color="action" /><Typography variant="body2">Судьи: {match.officials.map((o) => o.name).join(', ')}</Typography></Box>}

                <Box sx={{ mt: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Составы</Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: 2,
                      mt: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>{homeName}</Typography>
                      {homeRoster.length ? (
                        homeRoster.map((p) => (
                          <Typography key={p.id} variant="body2" color="text.secondary">
                            {`${p.firstName} ${p.lastName || ''}`.trim()}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">Состав не указан</Typography>
                      )}
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>{awayName}</Typography>
                      {awayRoster.length ? (
                        awayRoster.map((p) => (
                          <Typography key={p.id} variant="body2" color="text.secondary">
                            {`${p.firstName} ${p.lastName || ''}`.trim()}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">Состав не указан</Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </Box>
      </Paper>

      <Menu
        anchorEl={eventActionsAnchorEl}
        open={Boolean(eventActionsAnchorEl)}
        onClose={closeEventActions}
      >
        <MenuItem
          onClick={() => {
            if (activeEventForActions) {
              handleEditEvent(activeEventForActions);
            } else {
              closeEventActions();
            }
          }}
        >
          Изменить
        </MenuItem>
        <MenuItem
          onClick={async () => {
            if (activeEventForActions) {
              closeEventActions();
              await handleDeleteEvent(activeEventForActions.id);
              setActiveEventForActions(null);
            } else {
              closeEventActions();
            }
          }}
        >
          Удалить
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (activeEventForActions) {
              handleDuplicateEvent(activeEventForActions);
            } else {
              closeEventActions();
            }
          }}
        >
          Дублировать
        </MenuItem>
        <MenuItem
          disabled={!activeEventForActions || !isVarCancelableEvent(activeEventForActions) || isVarCancelled(activeEventForActions)}
          onClick={async () => {
            if (activeEventForActions) {
              await handleVarCancel(activeEventForActions);
            } else {
              closeEventActions();
            }
          }}
        >
          Отменить по VAR
        </MenuItem>
      </Menu>

      {/* Add Event Dialog */}
      <ResponsiveDialog
        open={showEventDialog}
        onClose={resetEventDialogState}
        title={eventDialogMode === 'edit' ? 'Изменить событие' : 'Добавить событие'}
        actions={
          <>
            <Button onClick={resetEventDialogState} sx={{ minHeight: 48 }}>Отмена</Button>
            <Button onClick={handleSubmitEvent} variant="contained" sx={{ minHeight: 48 }}>
              {eventDialogMode === 'edit' ? 'Сохранить' : 'Добавить'}
            </Button>
          </>
        }
      >
          <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2, mt: 1 }}>
            <InputLabel shrink>Тип события</InputLabel>
            <Select
              value={eventTypeKey}
              label="Тип события"
              onChange={(e) => {
                const selected = getEventOptionByKey(e.target.value);
                setEventTypeKey(selected.key);
                setEventForm({ ...eventForm, type: selected.value, actorId: undefined, targetId: undefined });
                if (selected.value !== 'goal') {
                  setEventOwnGoal(false);
                }
              }}
            >
              {eventTypeOptions.map((et) => <MenuItem key={et.key} value={et.key}>{et.label}</MenuItem>)}
            </Select>
          </FormControl>
          {eventForm.type === 'goal' && (
            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Checkbox
                  checked={eventOwnGoal}
                  onChange={(e) => {
                    setEventOwnGoal(e.target.checked);
                    setEventForm({ ...eventForm, actorId: undefined, targetId: undefined });
                  }}
                />
              }
              label="Автогол"
            />
          )}
          <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
            <InputLabel shrink>Команда</InputLabel>
            <Select
              value={eventTeamSide}
              label="Команда"
              onChange={(e) => {
                setEventTeamSide(e.target.value as EventTeamSide);
                setEventForm({ ...eventForm, actorId: undefined, targetId: undefined });
              }}
            >
              <MenuItem value="">Не указывать</MenuItem>
              <MenuItem value="home">{homeName}</MenuItem>
              <MenuItem value="away">{awayName}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            variant="outlined"
            size="small"
            label="Минута"
            type="number"
            fullWidth
            value={eventMinute}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => {
              setEventMinute(e.target.value);
              setEventForm({ ...eventForm, timestampSec: parseMinuteToSeconds(e.target.value) });
            }}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            sx={{ mb: 2 }}
          />
          {eventTypeKey === 'added_time' && (
            <TextField
              variant="outlined"
              size="small"
              label="Добавлено минут"
              type="number"
              fullWidth
              value={eventAddedTimeInput}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setEventAddedTimeInput(e.target.value)}
              onWheel={(e) => (e.target as HTMLInputElement).blur()}
              sx={{ mb: 2 }}
            />
          )}
          <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
            <InputLabel shrink>
              {eventForm.type === 'goal' && eventOwnGoal
                ? 'Автор автогола'
                : eventForm.type === 'substitution'
                  ? 'Кто ушел'
                  : 'Игрок/участник'}
            </InputLabel>
            <Select
              value={eventForm.actorId ?? ''}
              label={
                eventForm.type === 'goal' && eventOwnGoal
                  ? 'Автор автогола'
                  : eventForm.type === 'substitution'
                    ? 'Кто ушел'
                    : 'Игрок/участник'
              }
              onChange={(e) => setEventForm({ ...eventForm, actorId: e.target.value || undefined })}
            >
              <MenuItem value="">Не указывать</MenuItem>
              {actorOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {eventForm.type === 'substitution' && (
            <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
              <InputLabel shrink>Кто вышел</InputLabel>
              <Select
                value={eventForm.targetId ?? ''}
                label="Кто вышел"
                onChange={(e) => setEventForm({ ...eventForm, targetId: e.target.value || undefined })}
              >
                <MenuItem value="">Не указывать</MenuItem>
                {actorOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {eventForm.type === 'goal' && !eventOwnGoal && (
            <FormControl fullWidth variant="outlined" size="small" sx={{ mb: 2 }}>
              <InputLabel shrink>Ассистент (необязательно)</InputLabel>
              <Select
                value={eventForm.targetId ?? ''}
                label="Ассистент (необязательно)"
                onChange={(e) => setEventForm({ ...eventForm, targetId: e.target.value || undefined })}
              >
                <MenuItem value="">Не указывать</MenuItem>
                {assistantOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <TextField
            variant="outlined"
            size="small"
            label="Период"
            type="number"
            fullWidth
            value={eventPeriodInput}
            InputLabelProps={{ shrink: true }}
            onChange={(e) => {
              setEventPeriodInput(e.target.value);
            }}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
          />
      </ResponsiveDialog>
      {canManageMatch && match.status !== 'canceled' && match.status !== 'finished' && (
        <Box sx={{ position: 'sticky', bottom: { xs: `calc(${BOTTOM_NAV_OFFSET} + 16px)`, lg: 16 }, mt: 2, zIndex: 2 }}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            color="manage"
            startIcon={<PlayArrow />}
            onClick={() => navigate(`/matches/${id}/live`)}
            sx={{ minHeight: 52 }}
          >
            {liveLabel}
          </Button>
        </Box>
      )}
    </Box>
  );
};
